import React, { useEffect, useState, useRef } from "react";
import { api, fmt, setToken, getToken } from "@/api";
import { JerseyBadge, BigTimer } from "@/components/Shared";
import { LoginScreen } from "@/components/LoginScreen";

const SCORES = [];
for (let v = 0.5; v <= 10; v += 0.5) SCORES.push(Number(v.toFixed(1)));

export default function Judges() {
  const [user, setUser] = useState(null);
  const [authed, setAuthed] = useState(false);
  const [live, setLive] = useState(null);
  const [sched, setSched] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [selectedWave, setSelectedWave] = useState(0);
  const [flash, setFlash] = useState(null);
  const [toast, setToast] = useState("");
  const [eventLive, setEventLive] = useState(null);
  const poll = useRef(null);
  const judgeNum = user?.judge_num;

  const loadLive = async () => {
    try {
      const d = await api.live();
      setLive(d.live);
      setEventLive(d.event_live !== false);
    } catch (e) {}
  };

  const loadSched = async () => {
    try { setSched(await api.schedule()); } catch (e) {}
  };

  useEffect(() => {
    api.live().then((d) => setEventLive(d.event_live !== false)).catch(() => setEventLive(true));
    if (getToken()) {
      api.me().then((u) => {
        if (u.role === "judge" || u.role === "admin") { setUser(u); setAuthed(true); }
      }).catch(() => setToken(""));
    }
  }, []);

  useEffect(() => {
    if (authed) {
      const tick = () => { loadLive(); loadSched(); };
      tick();
      poll.current = setInterval(tick, 4000);
      return () => clearInterval(poll.current);
    }
  }, [authed]);

  useEffect(() => { if (!live) setPickerOpen(true); }, [live]);

  const startHeat = async (id) => {
    try {
      await api.openHeat(id);
      setSelectedRider(null);
      setPickerOpen(false);
      setToast("Heat is now LIVE for everyone watching");
      setTimeout(() => setToast(""), 1500);
      loadLive();
      loadSched();
    } catch (e) {
      setToast(e?.response?.data?.detail || "Couldn't start heat");
      setTimeout(() => setToast(""), 1600);
    }
  };

  const stopHeat = async (id) => {
    if (!window.confirm("Stop this heat? Scoring closes and results lock in.")) return;
    try {
      await api.closeHeat(id);
      setSelectedRider(null);
      setToast("Heat stopped");
      setTimeout(() => setToast(""), 1400);
      loadLive();
      loadSched();
    } catch (e) {
      setToast("Couldn't stop heat");
      setTimeout(() => setToast(""), 1600);
    }
  };

  const logout = () => { setToken(""); setUser(null); setAuthed(false); setSelectedRider(null); };

  const submitScore = async (value) => {
    if (!selectedRider || !live) return;
    try {
      await api.addScore({ heat_id: live.id, rider_id: selectedRider, value, wave_index: selectedWave });
      setFlash(value);
      setTimeout(() => setFlash(null), 350);
      setToast(`Wave ${selectedWave + 1} scored ${fmt(value)}`);
      setTimeout(() => setToast(""), 1200);
      loadLive();
    } catch (e) {
      setToast(e?.response?.data?.detail || "Failed — check connection");
      setTimeout(() => setToast(""), 1500);
    }
  };

  const undo = async () => {
    try {
      await api.undoScore({ heat_id: live.id, rider_id: selectedRider || undefined });
      setToast(selectedRider ? "Last wave removed" : "Last score removed");
      setTimeout(() => setToast(""), 1200);
      loadLive();
    } catch (e) {
      setToast("Nothing to undo");
      setTimeout(() => setToast(""), 1200);
    }
  };

  const startTimer = async (minutes) => {
    await api.startTimer(live.id, minutes);
    loadLive();
  };

  // ---- Not live yet ----
  if (eventLive === false) {
    return (
      <div className="max-w-md mx-auto p-4 pt-16 text-center" data-testid="judge-not-live">
        <div className="brutal p-8">
          <div className="font-score text-4xl">Judging Is Closed</div>
          <p className="font-display text-gray-600 mt-3 uppercase tracking-widest text-sm">
            The judges panel opens when the contest is set live from Setup.
          </p>
        </div>
      </div>
    );
  }

  // ---- Login ----
  if (!authed) {
    return (
      <LoginScreen
        title="Judge Login"
        subtitle="Sign in with your judge account"
        requiredRole="judge"
        onSuccess={(u) => { setUser(u); setAuthed(true); }}
      />
    );
  }

  // ---- Account has no judge seat ----
  if (!judgeNum) {
    return (
      <div className="max-w-md mx-auto p-4 pt-16 text-center" data-testid="judge-no-seat">
        <div className="brutal p-8">
          <div className="font-score text-3xl">No Judge Seat</div>
          <p className="font-display text-gray-600 mt-3 text-sm">
            This account isn't assigned to a judge seat (1, 2 or 3). Ask an organizer to set it up in Setup → Accounts.
          </p>
          <button data-testid="judge-logout" onClick={logout} className="mt-5 bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3">
            Log Out
          </button>
        </div>
      </div>
    );
  }

  // ---- Scoring screen ----
  return (
    <div className="max-w-lg mx-auto p-4 pb-28" data-testid="judge-scoring">
      <div className="flex items-center justify-between mb-3">
        <span className="brutal-2 bg-black text-white font-display font-black uppercase px-3 py-1 tracking-widest">
          Judge {judgeNum}
        </span>
        <button
          data-testid="judge-logout"
          onClick={logout}
          className="font-display text-xs uppercase tracking-widest underline text-gray-500"
        >
          Log out
        </button>
      </div>

      {/* Heat control — judges choose, start and stop heats */}
      <div className="brutal-2 p-3 mb-4" data-testid="heat-control">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display text-[10px] uppercase tracking-[0.25em] text-gray-500">Heat Control</div>
            <div className="font-display font-black uppercase text-sm truncate" data-testid="heat-control-status">
              {live ? `● Live: ${live.division_name} · ${live.name}` : "No heat live"}
            </div>
          </div>
          {live && (
            <button data-testid="stop-heat-btn" onClick={() => stopHeat(live.id)} className="brutal bg-[#E63946] text-white font-display font-black uppercase text-xs px-4 py-2 shrink-0 active:scale-95 transition-transform">■ Stop</button>
          )}
        </div>
        <button
          data-testid="toggle-heat-picker"
          onClick={() => setPickerOpen((o) => !o)}
          className="w-full mt-2 brutal bg-black text-white font-display font-black uppercase text-xs tracking-widest py-2 active:scale-95 transition-transform"
        >
          {pickerOpen ? "Hide heat list" : live ? "Change / start another heat" : "Choose a heat to start ▾"}
        </button>
        <p className="font-display text-[10px] text-gray-500 mt-1">Starting a heat makes it the live heat everyone watching sees.</p>

        {pickerOpen && sched && (
          <div className="mt-3 space-y-3 max-h-[24rem] overflow-y-auto pr-1" data-testid="heat-picker">
            {sched.divisions.map((d) => {
              const heats = (d.rounds || []).flatMap((rnd) => (rnd.heats || []).map((h) => ({ ...h, round_name: rnd.name })));
              if (heats.length === 0) return null;
              return (
                <div key={d.id}>
                  <div className="font-display font-black uppercase text-xs tracking-widest text-gray-600 mb-1">{d.name}</div>
                  <div className="space-y-1">
                    {heats.map((h) => {
                      const closed = h.state === "closed";
                      return (
                        <div
                          key={h.id}
                          data-testid={`picker-heat-${h.id}`}
                          className={`flex items-center gap-2 p-2 brutal-2 ${h.is_live ? "bg-red-50 border-[#E63946]" : closed ? "bg-gray-50" : "bg-white"}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold uppercase text-sm truncate">
                              {h.round_name} · {h.name}
                              {h.is_final && <span className="ml-1 text-[9px] bg-[#FFD700] text-black px-1 py-0.5 font-black">FINAL</span>}
                            </div>
                            <div className="font-display text-[10px] uppercase tracking-widest text-gray-400 truncate">
                              {(h.riders || []).map((r) => r.name).join(", ") || "No riders"}
                            </div>
                          </div>
                          {h.is_live ? (
                            <button data-testid={`picker-stop-${h.id}`} onClick={() => stopHeat(h.id)} className="brutal bg-[#E63946] text-white font-display font-black uppercase text-[10px] px-3 py-1 shrink-0">■ Stop</button>
                          ) : (
                            <button data-testid={`picker-start-${h.id}`} onClick={() => startHeat(h.id)} className={`brutal font-display font-black uppercase text-[10px] px-3 py-1 shrink-0 active:scale-95 transition-transform ${closed ? "bg-white" : "bg-green-600 text-white"}`}>
                              {closed ? "↻ Reopen" : "▶ Start"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!live ? (
        <div className="brutal p-8 text-center mt-4" data-testid="judge-no-live">
          <div className="font-score text-4xl">NO HEAT LIVE</div>
          <p className="font-display text-gray-500 uppercase text-xs tracking-widest mt-2">
            Pick a heat above to start scoring
          </p>
        </div>
      ) : (
        <>
          <div className="mb-2">
            <div className="font-display text-xs tracking-[0.25em] uppercase text-gray-500">{live.division_name}</div>
            <h1 className="font-display text-3xl font-black uppercase tracking-tight leading-none">{live.name}</h1>
          </div>

          <BigTimer timer={live.timer} />

          {!live.timer?.running && live.timer?.remaining_sec <= 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3" data-testid="timer-controls">
              {[10, 15, 20].map((m) => (
                <button
                  key={m}
                  data-testid={`start-timer-${m}`}
                  onClick={() => startTimer(m)}
                  className="brutal py-3 font-display font-black uppercase tracking-widest active:scale-95 transition-transform"
                >
                  {m} min
                </button>
              ))}
            </div>
          )}

          <h2 className="font-display text-sm uppercase tracking-widest text-gray-500 mt-5 mb-2">Tap the rider who rode</h2>
          <div className="grid grid-cols-2 gap-2" data-testid="rider-select">
            {live.riders.map((r) => {
              const active = selectedRider === r.rider_id;
              const lb = (live.leaderboard || []).find((x) => x.rider_id === r.rider_id);
              const wc = lb ? lb.wave_count : 0;
              const maxed = wc >= 10;
              return (
                <button
                  key={r.rider_id}
                  data-testid={`rider-btn-${r.jersey}`}
                  onClick={() => { setSelectedRider(r.rider_id); setSelectedWave(lb ? lb.wave_count : 0); }}
                  className={`brutal flex items-center gap-2 p-3 text-left active:scale-95 transition-transform ${
                    active ? "bg-black text-white" : "bg-white"
                  }`}
                >
                  <JerseyBadge jersey={r.jersey} size="sm" />
                  <span className="font-display font-bold uppercase truncate text-sm flex-1">{r.name}</span>
                  <span className={`font-display text-[10px] font-black shrink-0 ${maxed ? "text-[#E63946]" : (active ? "text-gray-300" : "text-gray-500")}`} data-testid={`wave-count-${r.jersey}`}>
                    {wc}/10
                  </span>
                </button>
              );
            })}
          </div>

          {selectedRider && (() => {
            const selLb = (live.leaderboard || []).find((x) => x.rider_id === selectedRider);
            const scored = new Set((selLb?.waves || []).map((w) => w.index));
            const waveCount = selLb ? selLb.wave_count : 0;
            const maxWave = Math.min(10, Math.max(waveCount + 1, 6));
            return (
              <div className="mt-4" data-testid="wave-picker">
                <h2 className="font-display text-sm uppercase tracking-widest text-gray-500 mb-2">Pick the wave</h2>
                <div className="grid grid-cols-5 gap-2">
                  {Array.from({ length: maxWave }, (_, i) => i).map((i) => {
                    const isSel = selectedWave === i;
                    const has = scored.has(i);
                    return (
                      <button
                        key={i}
                        data-testid={`wave-btn-${i + 1}`}
                        onClick={() => setSelectedWave(i)}
                        className={`brutal py-3 font-display font-black uppercase text-sm active:scale-95 transition-transform relative ${
                          isSel ? "bg-black text-white" : has ? "bg-[#FFD700] text-black" : "bg-white"
                        }`}
                      >
                        W{i + 1}
                        {has && <span className="absolute top-0.5 right-1 text-[9px]">✓</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="font-display text-[11px] text-gray-500 mt-1">Yellow = already has a score · re-tapping a wave updates your score for it.</p>
              </div>
            );
          })()}

          {selectedRider && (() => {
            const lb = (live.leaderboard || []).find((x) => x.rider_id === selectedRider);
            if (!lb) return null;
            const maxed = lb.wave_count >= 10;
            return (
              <div className="brutal-2 p-3 mt-3" data-testid="selected-rider-waves">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display text-xs uppercase tracking-widest text-gray-500">Waves ({lb.wave_count}/10) · top 2 count</span>
                  <span className="font-score text-2xl">{fmt(lb.total)}</span>
                </div>
                {lb.advancing === true ? (
                  <div className="font-display text-xs font-black uppercase tracking-widest text-green-600 mb-2" data-testid="selected-advancing">▲ Currently advancing</div>
                ) : lb.needs != null ? (
                  <div className="font-display text-xs font-black uppercase tracking-widest text-[#E63946] mb-2" data-testid="selected-needs">Needs {fmt(lb.needs)} more to advance</div>
                ) : null}
                {lb.waves.length === 0 ? (
                  <span className="font-display text-xs text-gray-400 uppercase tracking-widest">No waves yet</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {lb.waves.map((w) => (
                      <div key={w.index} className="text-center" data-testid={`wave-pill-${w.index}`}>
                        <div className="font-display text-[9px] uppercase tracking-widest text-gray-400 leading-none mb-0.5">W{w.index + 1}</div>
                        <span
                          className={`inline-block font-score text-lg px-2 py-0.5 border-2 ${w.counting ? "bg-[#FFD60A] border-black" : "border-gray-300 text-gray-400"}`}
                        >
                          {fmt(w.avg)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {maxed && <p className="font-display text-[11px] text-[#E63946] font-bold uppercase mt-2" data-testid="wave-max-note">10-wave max reached</p>}
              </div>
            );
          })()}

          <h2 className="font-display text-sm uppercase tracking-widest text-gray-500 mt-5 mb-2">
            {selectedRider ? `Tap the score for Wave ${selectedWave + 1}` : "Select a rider first"}
          </h2>
          <div className={`grid grid-cols-4 gap-2 ${!selectedRider ? "opacity-40 pointer-events-none" : ""}`} data-testid="score-pad">
            {SCORES.map((v) => {
              const excellent = v >= 8;
              return (
                <button
                  key={v}
                  data-testid={`score-${v}`}
                  onClick={() => submitScore(v)}
                  className={`brutal font-score text-3xl py-4 active:scale-95 transition-transform ${
                    flash === v ? "score-lock" : ""
                  } ${excellent ? "bg-[#FFD700] text-black" : "bg-white"}`}
                >
                  {fmt(v)}
                </button>
              );
            })}
          </div>

          <button
            data-testid="undo-btn"
            onClick={undo}
            className="w-full brutal bg-[#E63946] text-white font-display font-black uppercase tracking-widest py-4 mt-4 active:scale-95 transition-transform"
          >
            ↩ Undo Last Wave{selectedRider ? " (this rider)" : ""}
          </button>
        </>
      )}

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3 z-50" data-testid="judge-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
